import React from "react";
import { logger } from "@/lib/logger";
import { errorReference, reportClientError } from "@/lib/report-client-error";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isRetrying: boolean;
  showDetails: boolean;
  copied: boolean;
}


const isRecoverableVersionError = (msg: string) =>
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|_result\.default|Unable to preload CSS|Unable to preload module|reading 'component'|reading 'errorComponent'|reading 'pendingComponent'|reading 'notFoundComponent'|Rendered more hooks than during the previous render|Rendered fewer hooks than expected|change in the order of Hooks|ChunkLoadError|Loading chunk .* failed|Loading CSS chunk/i.test(
    msg,
  );

/** Deploy mid-session: the requested JS/CSS chunk no longer exists on the CDN. */
const isChunkLoadError = (error?: Error | null): boolean =>
  !!error &&
  (error.name === "ChunkLoadError" ||
    /ChunkLoadError|Loading chunk .* failed|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      error.message || "",
    ));

const isStale = (error?: Error | null): boolean =>
  isChunkLoadError(error) || isRecoverableVersionError(error?.message || "");

export class ErrorBoundaryWithLogger extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isRetrying: false, showDetails: false, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const route =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "-";
    const reference = errorReference(error.message || "");

    // Visible in the browser console (and forwarded to Sentry-style collectors).
    console.error(
      `[ErrorBoundary] ref=${reference} route=${route} name=${error.name}: ${error.message}`,
      error,
    );

    // Log to our internal logger
    logger.error("render", `ErrorBoundary caught: ${error.message}`, {
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack,
      route,
      reference,
      componentStack: errorInfo.componentStack,
    }, error.stack);

    this.setState({ errorInfo });

    void reportClientError(error, { componentStack: errorInfo.componentStack, boundary: "root" });

    if (isStale(error)) {
      // Chunk errors after a deploy: reload once, then show the card instead of looping.
      const KEY = "lemon_preload_reload_at";
      const ONCE_KEY = "lemon_chunk_reloaded";
      const last = Number(localStorage.getItem(KEY) || 0);
      const alreadyReloaded = sessionStorage.getItem(ONCE_KEY) === "1";

      if (!alreadyReloaded && Date.now() - last > 5 * 60 * 1000) {
        try {
          sessionStorage.setItem(ONCE_KEY, "1");
        } catch {}
        localStorage.setItem(KEY, String(Date.now()));
        void this.cleanAssets().finally(() => {
          const url = new URL(window.location.href);
          url.searchParams.set("_r", Date.now().toString(36));
          window.location.replace(url.toString());
        });
      }
    }
  }


  cleanAssets = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
    } catch {}
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {}
  };

  handleRetry = async () => {
    this.setState({ isRetrying: true });
    const stale = isStale(this.state.error);
    try {
      if (stale) await this.cleanAssets();
      localStorage.removeItem("lemon_preload_reload_at");
      sessionStorage.removeItem("lemon_chunk_reloaded");
    } catch {}
    const url = new URL(window.location.href);
    url.searchParams.set("_r", Date.now().toString(36));
    window.location.replace(url.toString());
  };

  detailText = () => {
    const e = this.state.error;
    return [
      `Referencia: ${e ? errorReference(e.message) : "-"}`,
      `Ruta: ${typeof window !== "undefined" ? window.location.pathname : "-"}`,
      `Mensaje: ${e?.message ?? "-"}`,
      `Stack:\n${(e?.stack ?? "-").split("\n").slice(0, 12).join("\n")}`,
      `Componente:\n${(this.state.errorInfo?.componentStack ?? "-").split("\n").slice(0, 12).join("\n")}`,
    ].join("\n\n");
  };

  handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(this.detailText());
      this.setState({ copied: true });
    } catch {}
  };

  override render() {
    if (this.state.hasError) {
      const stale = isStale(this.state.error);
      const reference = this.state.error ? errorReference(this.state.error.message) : null;
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">🍋</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Algo no se ha cargado bien</h1>
            <p className="text-gray-600 mb-6">
              {stale
                ? "Parece que el móvil tenía una versión anterior guardada. Reintenta para cargar la versión nueva."
                : "Hemos detectado un error. Recarga la página para continuar — si persiste, contacta con soporte."}
            </p>
            {reference && (
              <p className="mb-4 text-xs text-gray-500">Referencia: {reference}</p>
            )}

            {this.state.error && (
              <div className="mb-6 text-left">
                <button
                  onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
                  className="text-xs text-gray-500 underline"
                >
                  {this.state.showDetails ? "Ocultar detalle técnico" : "Ver detalle técnico"}
                </button>
                {this.state.showDetails && (
                  <div className="mt-2 bg-red-50 p-3 rounded border border-red-200">
                    <pre className="text-[11px] font-mono text-red-900 whitespace-pre-wrap break-words max-h-64 overflow-auto">
                      {this.detailText()}
                    </pre>
                    <button
                      onClick={this.handleCopy}
                      className="mt-2 text-xs bg-white border border-red-200 rounded px-2 py-1 text-red-900"
                    >
                      {this.state.copied ? "Copiado ✓" : "Copiar detalle"}
                    </button>
                  </div>
                )}
              </div>
            )}



            <div className="flex flex-col gap-2">
              <button
                onClick={this.handleRetry}
                disabled={this.state.isRetrying}
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-2 px-4 rounded transition-colors"
              >
                {this.state.isRetrying ? "Reintentando…" : "Reintentar carga"}
              </button>
              <button
                onClick={() => window.location.href = "/"}
                className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-2 px-4 rounded transition-colors"
              >
                Ir al inicio
              </button>
              {process.env['NODE_ENV'] === "development" && (
                <button
                  onClick={() => window.location.href = "/debug/logs"}
                  className="bg-blue-200 hover:bg-blue-300 text-blue-900 font-semibold py-2 px-4 rounded transition-colors text-sm"
                >
                  Ver logs de debug
                </button>
              )}
            </div>

            {process.env['NODE_ENV'] === "development" && (
              <div className="mt-6 text-xs text-gray-500 text-left p-4 bg-gray-100 rounded">
                <p className="font-semibold mb-2">Información para desarrolladores:</p>
                <p>El error ha sido registrado automáticamente en el sistema de logs interno.</p>
                <p className="mt-1">Puedes revisar los detalles completos en <code className="font-mono">/debug/logs</code></p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryWithLogger;
